import React from 'react'
import { compose, graphql } from 'react-apollo'
import { withRouter } from 'react-router-dom'
import { Button, TextInput } from 'carbon-components-react'
import * as Errors from '../utils/errors'
import { Elements, StripeProvider, CardElement, injectStripe } from 'react-stripe-elements'

import AppBranding from '../components/AppBranding'
import { SelectASync } from '../components/Select'
import WithAlertModal from '../hocs/WithAlertModal'
import GetUser from '../hocs/queries/GetUser'
import GetPlatformImages from '../hocs/queries/GetPlatformImages'
import ApolloClient from '../utils/Apollo'
import { decodeHtmlEntities } from '../utils/decodeHtml'
import BackgroundImage from '../assets/images/veva-collect-default-download-image.jpg'

import { UPDATE_USER, DO_SUBSCRIPTION_UPDATE, DO_SUBSCRIPTION_BILLING_UPDATE } from '../graphql/mutations'
import { GET_TIMEZONE_TYPES, VALIDATE_DISCOUNT_CODE } from '../graphql/queries'
import {
  PLAN_FREE,
  PLAN_INDIVIDUAL,
  PLAN_PRO,
  PLAN_PRO_UNLIMITED,
  PLAN_NAMES,
  PLAN_PRICES
} from '../utils/constants'

const cardElementStyle = {
  base: {
    color: 'white',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    '::placeholder': { color: '#666' }
  },
  invalid: {
    color: '#e0182d',
    iconColor: '#e0182d'
  }
}

class OnboardingForm extends React.PureComponent {
  _submitting = false

  state = {
    timezoneId: '',
    plan: 'free',
    discountCode: '',
    validDiscountCode: false,
    validationMessage: '',
    type: '',
    loading: false,
    errors: {},
    individual: PLAN_PRICES.individual,
    pro: PLAN_PRICES.pro,
    prounlimited: PLAN_PRICES.prounlimited,
    firstName: '',
    lastName: ''
  }

  constructor(props) {
    super(props)
    const params = new URLSearchParams(props.location.search)
    const code = params.get('code') || localStorage.getItem('onboarding_discount_code') || ''
    if (code) {
      this.state.discountCode = code
      this.state.plan = 'individual'
    }
    // Pre-fill name fields from existing user data (Google/Facebook populate these;
    // Apple subsequent auths leave them blank, prompting the user to fill them in)
    this.state.firstName = (props.user && props.user.firstName) || ''
    this.state.lastName = (props.user && props.user.lastName) || ''
  }

  componentDidMount() {
    if (this.state.discountCode) {
      this.validateDiscountCode(this.state.discountCode)
    }
  }

  getTimezonesTypeOptions = (inputValue) =>
    new Promise(resolve => {
      ApolloClient.query({
        query: GET_TIMEZONE_TYPES,
        fetchPolicy: 'network-only'
      }).then(({ data }) => {
        let timezoneTypes = []
        data.getTimezone.forEach(timezoneType => {
          if (!timezoneType.timezone.toLowerCase().includes(inputValue.toLowerCase())) {
            return
          }
          timezoneTypes.push({
            value: timezoneType.id,
            label: decodeHtmlEntities(timezoneType.offset)
          })
        })
        resolve(timezoneTypes)
      })
    })

  handleChangeTimezone = (ev) => {
    this.setState({
      timezoneId: ev.value,
      errors: { ...this.state.errors, timezoneId: undefined }
    })
  }

  handleNameChange = (field) => (event) => {
    this.setState({
      [field]: event.target.value,
      errors: { ...this.state.errors, [field]: undefined }
    })
  }

  handlePercentOff = (plan, percentOff) => {
    const price = parseFloat(PLAN_PRICES[plan].replace('$', ''))
    const discounted = (price - (price * (percentOff / 100))).toFixed(2)
    this.setState({ [plan]: '$' + discounted })
  }

  handleAmountOff = (plan, amountOff) => {
    const price = parseFloat(PLAN_PRICES[plan].replace('$', ''))
    const discounted = Math.max(0, price - parseFloat(amountOff)).toFixed(2)
    this.setState({ [plan]: '$' + discounted })
  }

  validateDiscountCode = async (code) => {
    if (!code) {
      this.setState({ type: '', validationMessage: '', validDiscountCode: false })
      return
    }

    try {
      const { data } = await ApolloClient.query({
        query: VALIDATE_DISCOUNT_CODE,
        variables: { couponCode: code },
        fetchPolicy: 'network-only'
      })

      const { valid, percentOff, amountOff, type, message } = data.validateCouponCode
      this.setState({ validDiscountCode: valid, type })

      if (type === 'referral' || !type) {
        this.setState({ validationMessage: message })
      } else {
        this.setState({ validationMessage: '' })
      }

      const plans = ['pro', 'individual', 'prounlimited']
      if (percentOff) {
        plans.forEach(p => this.handlePercentOff(p, percentOff))
      }
      if (amountOff) {
        plans.forEach(p => this.handleAmountOff(p, amountOff))
      }
    } catch {
      this.setState({ validDiscountCode: false, validationMessage: 'Invalid code' })
    }
  }

  handleDiscountCodeChange = async (event) => {
    const { value } = event.target
    const newPlan = value ? 'individual' : 'free'
    this.setState({
      discountCode: value,
      plan: newPlan,
      validationMessage: '',
      individual: PLAN_PRICES.individual,
      pro: PLAN_PRICES.pro,
      prounlimited: PLAN_PRICES.prounlimited
    })
    if (this.props.onPlanChange) {
      this.props.onPlanChange(newPlan)
    }

    if (value) {
      await this.validateDiscountCode(value)
    }
  }

  handleSelectPlan = (plan) => {
    this.setState({
      plan,
      individual: PLAN_PRICES.individual,
      pro: PLAN_PRICES.pro,
      prounlimited: PLAN_PRICES.prounlimited
    })
    if (this.props.onPlanChange) {
      this.props.onPlanChange(plan)
    }
  }

  finishOnboarding = () => {
    localStorage.removeItem('sso_needs_onboarding')
    localStorage.removeItem('onboarding_discount_code')
    this.props.history.push('/')
  }

  handleSubmit = async (e) => {
    e.preventDefault()

    if (this._submitting) return
    this._submitting = true

    const { timezoneId, plan, discountCode, validDiscountCode, type, firstName, lastName } = this.state
    const { user } = this.props

    const errors = {}
    if (!timezoneId) errors.timezoneId = Errors.Required
    if (!firstName.trim()) errors.firstName = Errors.Required
    if (!lastName.trim()) errors.lastName = Errors.Required

    if (Object.keys(errors).length > 0) {
      this._submitting = false
      this.setState({ errors })
      return
    }

    this.setState({ loading: true, errors: {} })

    try {
      // 1. Update timezone + name (pass required fields)
      await this.props.updateUser({
        variables: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: user.email,
          timezoneId
        }
      })

      // 2. If paid plan, create Stripe token and update billing first
      if (plan !== 'free') {
        const { token, error } = await this.props.stripe.createToken()
        if (error) {
          this.setState({ loading: false, errors: { card: error.message } })
          return
        }

        await this.props.updateBilling({
          variables: { stripeToken: token.id }
        })
      }

      // 3. Always update subscription (triggers plan email after onboarding)
      const subscriptionVars = { plan }
      if (plan !== 'free' && discountCode && validDiscountCode) {
        if (type === 'referral') {
          subscriptionVars.referralCode = discountCode
        } else {
          subscriptionVars.coupon = discountCode
        }
      }
      await this.props.updateSubscription({
        variables: subscriptionVars
      })

      this.finishOnboarding()
    } catch {
      this._submitting = false
      this.setState({ loading: false })
      this.props.alert('Something went wrong. Please try again.', {
        title: 'Error'
      })
    }
  }

  render() {
    const {
      plan,
      discountCode,
      errors,
      loading,
      validDiscountCode,
      type,
      validationMessage,
      firstName,
      lastName
    } = this.state

    const { user } = this.props
    const disableFree = !!discountCode && validDiscountCode
    const needsName = !user || !user.firstName || !user.lastName

    return (
      <form onSubmit={this.handleSubmit} className='onboarding-form'>
        {needsName && (
          <React.Fragment>
            <div className='formRow'>
              <TextInput
                id='firstName'
                name='firstName'
                labelText='First Name *'
                value={firstName}
                onChange={this.handleNameChange('firstName')}
                invalid={errors.firstName ? true : false}
                invalidText={errors.firstName || ''}
              />
            </div>
            <div className='formRow'>
              <TextInput
                id='lastName'
                name='lastName'
                labelText='Last Name *'
                value={lastName}
                onChange={this.handleNameChange('lastName')}
                invalid={errors.lastName ? true : false}
                invalidText={errors.lastName || ''}
              />
            </div>
          </React.Fragment>
        )}
        <div className='formRow'>
          <SelectASync
            labelText='Time Zone *'
            id='timeZone'
            cacheOptions
            defaultOptions
            loadOptions={this.getTimezonesTypeOptions}
            onChange={this.handleChangeTimezone}
            invalid={errors.timezoneId ? true : false}
            invalidText={errors.timezoneId || ''}
          />
        </div>

        <div className='formRow'>
          <label className='bx--label'>Select Your Plan</label>
          <div className='plans-container'>
            {Object.keys(PLAN_NAMES).map(key => {
              let planname = ''
              let message = ''
              if (key === PLAN_FREE) {
                planname = 'free'
                message = 'No money, no problem.'
              } else if (key === PLAN_INDIVIDUAL) {
                planname = 'individual'
                message = 'For the avid music creator.'
              } else if (key === PLAN_PRO) {
                planname = 'pro'
                message = 'For powerful collaboration features.'
              } else if (key === PLAN_PRO_UNLIMITED) {
                planname = 'prounlimited'
                message = 'Unlimited storage for unlimited creativity.'
              }

              const isDisabled = disableFree && key === PLAN_FREE

              return (
                <div className={'plan-container ' + (isDisabled ? 'disabled-plan' : '')} key={key}>
                  <div className='plan-info'>
                    <div className='plan-name'>{PLAN_NAMES[key]}</div>
                    <div className='plan-detail'>{message}</div>
                  </div>
                  <button
                    type='button'
                    onClick={() => this.handleSelectPlan(planname)}
                    disabled={isDisabled}
                    className={
                      'plan-option ' + (plan === key ? 'selected-plan-option ' : '')
                    }
                  >
                    {key === PLAN_FREE ? PLAN_PRICES[key] : this.state[planname] || PLAN_PRICES[key]}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        <div className='formRow'>
          <label className='bx--label'>Discount/Referral Code</label>
          <input
            className='bx--text-input'
            type='text'
            name='discountCode'
            value={discountCode}
            onChange={this.handleDiscountCodeChange}
            placeholder='Enter code (optional)'
          />
          {discountCode && (
            <div className='show-validate-message'>
              {validDiscountCode && type === 'referral' ? (
                <p>{`1 Free Month of any plan. ${PLAN_NAMES[plan]} Plan selected. (${PLAN_PRICES[plan]} after)`}</p>
              ) : (
                <p>{validationMessage}</p>
              )}
            </div>
          )}
        </div>

        {plan !== 'free' && (
          <div className='formRow'>
            <label className='bx--label'>Card Details</label>
            <CardElement style={cardElementStyle} />
            {errors.card && (
              <div className='bx--form-requirement visible'>{errors.card}</div>
            )}
          </div>
        )}

        <div className='formRow onboarding-actions'>
          <Button type='submit' className='loginSubmit' disabled={loading}>
            {loading ? 'Setting up...' : 'Continue'}
          </Button>
        </div>
      </form>
    )
  }
}

const OnboardingFormWithStripe = compose(
  injectStripe,
  WithAlertModal,
  withRouter,
  graphql(UPDATE_USER, { name: 'updateUser' }),
  graphql(DO_SUBSCRIPTION_BILLING_UPDATE, { name: 'updateBilling' }),
  graphql(DO_SUBSCRIPTION_UPDATE, { name: 'updateSubscription' })
)(OnboardingForm)

class OnboardingInner extends React.Component {
  state = { tier: 'free' }

  renderFeatures = (features) => {
    return { __html: features }
  }

  render() {
    const { user, platformImages } = this.props
    const [image] = (platformImages || [])
      .filter(i => i.location === 'registration' && i.tier === this.state.tier)

    const planName = image && image.name === 'Pro Unlimited' ? (<>
      <p className='pro'>Pro </p><p>Unlimited</p></>) : (image && image.name)

    return (
      <StripeProvider apiKey={process.env.REACT_APP_STRIPE_KEY}>
        <div className='login'>
          <div className='loginFormBackground onboarding-page'>
            <div className='loginFormWrapper'>
              <AppBranding />
              <div className='loginForm onboarding-card'>
                <h2 className='onboarding-title'>Set up your account</h2>
                <p className='onboarding-subtitle'>Choose your timezone and plan to get started.</p>
                <Elements>
                  <OnboardingFormWithStripe
                    user={user}
                    onPlanChange={(plan) => this.setState({ tier: plan })}
                  />
                </Elements>
              </div>
            </div>
          </div>
          <div className='loginBackground'>
            {image ? (
              <div className='test-wrapper'>
                <img src={image.fullPath} alt={image.altText} />
                <div className='text-overlay'>
                  <div className='content-wrapper'>
                    <div className='header'>
                      <div className='header-plan'>
                        <p className='header-plan-name'>{image.name ? planName : ''}</p>
                        <p className='header-plan-price'>{image.price ? image.price : ''}</p>
                      </div>
                      <div className={`header-caption ${image.name === 'Pro Unlimited' ? 'unlimited' : ''}`}>
                        <p>{image.use ? image.use : ''}</p>
                        <a
                          className='compare-plans loginSubmit register-submit'
                          target='_blank'
                          href='https://vevacollect.com/pricing'
                          rel='noopener noreferrer'>
                          Compare Plans
                        </a>
                      </div>
                    </div>
                    <div className='bullets'>
                      <div dangerouslySetInnerHTML={this.renderFeatures(image.features)} />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <img src={BackgroundImage} alt='Onboarding' />
            )}
            {image && image.caption && <div className='loginBackgroundCredit'>{image.caption}</div>}
          </div>
        </div>
      </StripeProvider>
    )
  }
}

export default compose(
  GetUser,
  GetPlatformImages
)(OnboardingInner)
